import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';

import { Agents } from '@air/api';
import { type ListAgentSessionsResponse } from '@air/server-sdk';
import { callAirApi } from '@air/utils-api';

import {
  buildPrivateWorkspaceAgentSessionsListArchivedParams,
  buildPrivateWorkspaceAgentSessionsListInfiniteParams,
  getPrivateWorkspaceAgentSessionsListInfiniteKey,
} from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionsListInfinite';
import { buildWorkspaceAgentSessionListCachePatch } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentSessionListCachePatch';
import { patchPrivateWorkspaceAgentSessionsListCache } from '~/components/v2/components/workspace/workspace-agent-panel/utils/patchPrivateWorkspaceAgentSessionsListCache';

export type UsePrivateWorkspaceAgentSessionUpdateMutationParams = NonNullable<
  Parameters<typeof Agents.Sessions.update>[0]
>;

export const usePrivateWorkspaceAgentSessionUpdateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UsePrivateWorkspaceAgentSessionUpdateMutationParams) =>
      callAirApi(() => Agents.Sessions.update(params)),
    onMutate: async (variables) => {
      const liveListParams = buildPrivateWorkspaceAgentSessionsListInfiniteParams(variables.workspaceId);
      const archivedListParams = buildPrivateWorkspaceAgentSessionsListArchivedParams(variables.workspaceId);
      const liveListKey = getPrivateWorkspaceAgentSessionsListInfiniteKey(liveListParams);
      const archivedListKey = getPrivateWorkspaceAgentSessionsListInfiniteKey(archivedListParams);
      const patch = buildWorkspaceAgentSessionListCachePatch(new Date().toISOString(), { title: variables.title });

      await Promise.all([
        queryClient.cancelQueries({ queryKey: liveListKey }),
        queryClient.cancelQueries({ queryKey: archivedListKey }),
      ]);

      const previousLiveList = queryClient.getQueryData<InfiniteData<ListAgentSessionsResponse>>(liveListKey);
      const previousArchivedList = queryClient.getQueryData<InfiniteData<ListAgentSessionsResponse>>(archivedListKey);

      patchPrivateWorkspaceAgentSessionsListCache(queryClient, liveListParams, variables.sessionId, patch);
      patchPrivateWorkspaceAgentSessionsListCache(queryClient, archivedListParams, variables.sessionId, patch);

      return { liveListKey, archivedListKey, previousLiveList, previousArchivedList };
    },
    onError: (_error, _variables, context) => {
      if (!context) {
        return;
      }

      queryClient.setQueryData(context.liveListKey, context.previousLiveList);
      queryClient.setQueryData(context.archivedListKey, context.previousArchivedList);
    },
    onSuccess: (updatedSession, variables) => {
      const liveListParams = buildPrivateWorkspaceAgentSessionsListInfiniteParams(variables.workspaceId);
      const archivedListParams = buildPrivateWorkspaceAgentSessionsListArchivedParams(variables.workspaceId);
      const patch = buildWorkspaceAgentSessionListCachePatch(updatedSession.updatedAt, { title: updatedSession.title });

      patchPrivateWorkspaceAgentSessionsListCache(queryClient, liveListParams, variables.sessionId, patch);
      patchPrivateWorkspaceAgentSessionsListCache(queryClient, archivedListParams, variables.sessionId, patch);
    },
  });
};
